import { useState, useEffect, useCallback } from 'react';
import {
  Table, TableHeader, TableRow, TableHeaderCell, TableBody, TableCell,
  Button, Spinner, Text, Badge, Checkbox, Input, makeStyles,
  MessageBar, MessageBarBody, MessageBarTitle,
  Dialog, DialogSurface, DialogBody, DialogTitle, DialogContent, DialogActions,
} from '@fluentui/react-components';
import { Search24Regular, CheckmarkCircle24Regular } from '@fluentui/react-icons';
import { useMsal } from '@azure/msal-react';
import { useNavigate } from 'react-router-dom';
import { useApiClient, protectedResources } from '../../services/apiService';
import { SPACING } from '../../styles/constants';

interface DirectoryUser {
  id: string;
  displayName: string;
  email: string;
  userPrincipalName: string;
  jobTitle: string | null;
  department: string | null;
}

interface License {
  id: string;
  userObjectId: string;
  email: string;
  displayName: string;
  jobTitle: string | null;
  department: string | null;
  assignedAt: string;
  assignedBy: string;
}

const useStyles = makeStyles({
  container: { maxWidth: '1200px', padding: SPACING.lg },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: SPACING.xl },
  searchRow: { display: 'flex', gap: SPACING.sm, alignItems: 'center', marginBottom: SPACING.md },
  summary: { display: 'flex', gap: SPACING.lg, marginBottom: SPACING.lg, alignItems: 'center' },
  actions: { display: 'flex', gap: SPACING.sm },
  row: { cursor: 'pointer' },
  consentMessage: { marginBottom: SPACING.lg },
});

export default function UserManagement() {
  const styles = useStyles();
  const api = useApiClient();
  const { instance, accounts } = useMsal();
  const navigate = useNavigate();

  const [directoryUsers, setDirectoryUsers] = useState<DirectoryUser[]>([]);
  const [licenses, setLicenses] = useState<License[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchFilter, setSearchFilter] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [consentRequired, setConsentRequired] = useState(false);
  const [trialStatus, setTrialStatus] = useState<any>(null);
  const [showTrialPopup, setShowTrialPopup] = useState(false);

  const licensedUserIds = new Set(licenses.map(l => l.userObjectId));

  // Acquire a Graph token for the current user
  const getGraphToken = useCallback(async (): Promise<string | null> => {
    try {
      const account = accounts[0];
      if (!account) return null;
      const response = await instance.acquireTokenSilent({
        scopes: protectedResources.graph.scopes,
        account,
      });
      return response.accessToken;
    } catch {
      // If silent fails, try popup
      try {
        const response = await instance.acquireTokenPopup({
          scopes: protectedResources.graph.scopes,
        });
        return response.accessToken;
      } catch {
        return null;
      }
    }
  }, [instance, accounts]);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');
    setConsentRequired(false);

    try {
      // Load licenses first. Trial status is helpful for UX, but the page should
      // still work if that endpoint is not yet available on the deployed API.
      const licRes = await api.get('/tenant/licenses');
      setLicenses(Array.isArray(licRes.data) ? licRes.data : []);

      try {
        const trialRes = await api.get('/tenant/trial-status');
        setTrialStatus(trialRes.data);

        // Show trial acceptance popup if trial not yet accepted
        if (trialRes.data?.isTrial && !trialRes.data?.trialAccepted) {
          setShowTrialPopup(true);
        }
      } catch (trialErr: any) {
        if (trialErr?.response?.status === 404) {
          setTrialStatus(null);
          setShowTrialPopup(false);
        } else {
          throw trialErr;
        }
      }

      // Get Graph token and fetch directory users
      const graphToken = await getGraphToken();
      if (!graphToken) {
        setConsentRequired(true);
        setLoading(false);
        return;
      }

      const dirRes = await api.get('/tenant/directory-users', {
        headers: { Authorization: `Bearer ${graphToken}` },
      });
      setDirectoryUsers(Array.isArray(dirRes.data) ? dirRes.data : []);
    } catch (err: any) {
      if (err?.response?.status === 403) {
        setConsentRequired(true);
      } else {
        setError(err?.response?.data?.error || 'Failed to load users');
      }
    } finally {
      setLoading(false);
    }
  }, [api, getGraphToken]);

  useEffect(() => { loadData(); }, [loadData]);

  // Initialize selectedIds from current licenses when data loads
  useEffect(() => {
    setSelectedIds(new Set(licenses.map(l => l.userObjectId)));
  }, [licenses]);

  const handleToggleUser = (userId: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(userId)) {
        next.delete(userId);
      } else {
        next.add(userId);
      }
      return next;
    });
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedIds(new Set(filteredUsers.map(u => u.id)));
    } else {
      setSelectedIds(new Set());
    }
  };

  const hasChanges = () => {
    const currentLicensedIds = new Set(licenses.map(l => l.userObjectId));
    if (selectedIds.size !== currentLicensedIds.size) return true;
    for (const id of selectedIds) {
      if (!currentLicensedIds.has(id)) return true;
    }
    return false;
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccessMsg('');

    try {
      const currentLicensedIds = new Set(licenses.map(l => l.userObjectId));

      // Users to assign (selected but not currently licensed)
      const toAssign = directoryUsers.filter(u => selectedIds.has(u.id) && !currentLicensedIds.has(u.id));
      // Users to revoke (currently licensed but not selected)
      const toRevoke = licenses.filter(l => !selectedIds.has(l.userObjectId)).map(l => l.userObjectId);

      if (toAssign.length > 0) {
        await api.post('/tenant/licenses', {
          users: toAssign.map(u => ({
            userObjectId: u.id,
            email: u.email,
            displayName: u.displayName,
            jobTitle: u.jobTitle,
            department: u.department,
          })),
        });
      }

      if (toRevoke.length > 0) {
        await api.delete('/tenant/licenses', { data: { userObjectIds: toRevoke } });
      }

      setSuccessMsg(`Updated: ${toAssign.length} assigned, ${toRevoke.length} revoked`);
      await loadData();
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const handleConsent = async () => {
    setError('');
    try {
      await instance.acquireTokenPopup({
        scopes: protectedResources.graph.scopes,
      });
      setConsentRequired(false);
      await loadData();
    } catch {
      setError('Directory access was not granted. Sherpai will still work, but only a Microsoft admin can unlock User Management for your organisation.');
    }
  };

  const handleAcceptTrial = async () => {
    try {
      await api.post('/tenant/trial-accept', { accepted: true });
      setShowTrialPopup(false);
      setTrialStatus((prev: any) => ({ ...prev, trialAccepted: true }));
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to accept trial terms');
    }
  };

  // Filter users by search
  const filteredUsers = directoryUsers.filter(u => {
    if (!searchFilter) return true;
    const q = searchFilter.toLowerCase();
    return (
      u.displayName?.toLowerCase().includes(q) ||
      u.email?.toLowerCase().includes(q) ||
      u.jobTitle?.toLowerCase().includes(q) ||
      u.department?.toLowerCase().includes(q)
    );
  });

  if (loading) return <Spinner label="Loading users from your directory..." />;

  if (consentRequired) {
    return (
      <div className={styles.container}>
        <Text size={500} weight="semibold" block style={{ marginBottom: '16px' }}>User Management</Text>
        <MessageBar intent="warning" className={styles.consentMessage}>
          <MessageBarBody>
            <MessageBarTitle>Directory access required</MessageBarTitle>
            To show your organisation&apos;s people in User Management, Sherpai needs one-time,
            read-only Microsoft Graph consent for <strong>User.Read.All</strong>. This is only needed
            for directory-backed user management and does not block the rest of the portal.
          </MessageBarBody>
        </MessageBar>
        {error && (
          <MessageBar intent="error" style={{ marginBottom: '12px' }}>
            <MessageBarBody>{error}</MessageBarBody>
          </MessageBar>
        )}
        <div className={styles.actions}>
          <Button appearance="primary" onClick={handleConsent}>
            Grant directory access
          </Button>
          <Button appearance="secondary" onClick={() => navigate('/customer/dashboard')}>
            Back to dashboard
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <Text size={500} weight="semibold">User Management</Text>
        <div className={styles.actions}>
          <Button
            appearance="primary"
            disabled={!hasChanges() || saving}
            onClick={handleSave}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </div>

      <div className={styles.summary}>
        <Badge appearance="filled" color="brand" size="large">
          {selectedIds.size} licensed
        </Badge>
        <Badge appearance="outline" size="large">
          {directoryUsers.length} total in directory
        </Badge>
        {trialStatus?.isTrial && (
          <Badge appearance="outline" color="warning" size="large">
            Trial: {trialStatus.daysRemaining} days remaining · max {trialStatus.trialMaxUsers} users
          </Badge>
        )}
      </div>

      {error && (
        <MessageBar intent="error" style={{ marginBottom: '12px' }}>
          <MessageBarBody>{error}</MessageBarBody>
        </MessageBar>
      )}
      {successMsg && (
        <MessageBar intent="success" style={{ marginBottom: '12px' }}>
          <MessageBarBody>{successMsg}</MessageBarBody>
        </MessageBar>
      )}

      <div className={styles.searchRow}>
        <Search24Regular />
        <Input
          placeholder="Search by name, email, title, or department..."
          value={searchFilter}
          onChange={(_, v) => setSearchFilter(v.value)}
          style={{ minWidth: '300px' }}
        />
        <Text size={200}>{filteredUsers.length} shown</Text>
      </div>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHeaderCell style={{ width: '48px' }}>
              <Checkbox
                checked={filteredUsers.length > 0 && filteredUsers.every(u => selectedIds.has(u.id))}
                onChange={(_, d) => handleSelectAll(!!d.checked)}
              />
            </TableHeaderCell>
            <TableHeaderCell>Name</TableHeaderCell>
            <TableHeaderCell>Email</TableHeaderCell>
            <TableHeaderCell>Job Title</TableHeaderCell>
            <TableHeaderCell>Department</TableHeaderCell>
            <TableHeaderCell>Status</TableHeaderCell>
          </TableRow>
        </TableHeader>
        <TableBody>
          {filteredUsers.map((u) => (
            <TableRow key={u.id} className={styles.row} onClick={() => handleToggleUser(u.id)}>
              <TableCell>
                <Checkbox
                  checked={selectedIds.has(u.id)}
                  onChange={() => handleToggleUser(u.id)}
                />
              </TableCell>
              <TableCell>{u.displayName}</TableCell>
              <TableCell>{u.email}</TableCell>
              <TableCell>{u.jobTitle || '—'}</TableCell>
              <TableCell>{u.department || '—'}</TableCell>
              <TableCell>
                {licensedUserIds.has(u.id) ? (
                  <Badge appearance="filled" color="success" icon={<CheckmarkCircle24Regular />}>
                    Licensed
                  </Badge>
                ) : selectedIds.has(u.id) ? (
                  <Badge appearance="outline" color="brand">
                    Pending
                  </Badge>
                ) : (
                  <Badge appearance="outline" color="subtle">
                    No access
                  </Badge>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {/* Trial Acceptance Dialog */}
      <Dialog open={showTrialPopup} modalType="alert">
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Welcome to your Sherpai Trial</DialogTitle>
            <DialogContent>
              <Text block style={{ marginBottom: '12px' }}>
                You have a <strong>7-day free trial</strong> for up to <strong>5 users</strong>.
              </Text>
              <Text block style={{ marginBottom: '12px' }}>
                If any users remain licensed after the trial period ends, you agree to pay
                for those users at the rates shown on the Subscription page.
              </Text>
              <Text block weight="semibold">
                By clicking &quot;Accept &amp; Continue&quot;, you agree to these terms.
              </Text>
            </DialogContent>
            <DialogActions>
              <Button appearance="primary" onClick={handleAcceptTrial}>
                Accept &amp; Continue
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
}
